import {
    IConnectionTemplateConnectionGroupDTO,
    IOtherConnection,
    IRoleConnectionAssignment,
    IRoleConnectionInput,
    IRoleConnectionsValue,
    RoleConnectionApiRole,
    RoleConnectionKey,
    RoleConnectionTargetType,
    TConnection,
} from '../../../../../api/blueprints/connection.template.types';
import { getConnectionTemplateItemLabelText } from '../../../../../infrastructure/utils/connections.utils';

export const ROLE_CONNECTION_KEYS: RoleConnectionKey[] = ['presenter', 'host', 'support'];

const ROLE_CONNECTION_API_ROLES: Record<RoleConnectionKey, RoleConnectionApiRole> = {
    presenter: 'PRESENTER',
    host: 'HOST',
    support: 'SUPPORT',
};

const ROLE_CONNECTION_KEYS_BY_API_ROLE: Record<RoleConnectionApiRole, RoleConnectionKey> = {
    PRESENTER: 'presenter',
    HOST: 'host',
    SUPPORT: 'support',
};

export const CHOOSE_CONNECTION_OPTION_ID = '';

export interface IRoleConnectionOption {
    id: string;
    label: string;
    isGroup: boolean;
}

export const emptyRoleConnections = (): IRoleConnectionsValue => ({
    presenter: null,
    host: null,
    support: null,
});

export const normalizeRoleConnections = (value?: IRoleConnectionsValue | null): IRoleConnectionsValue => ({
    presenter: value?.presenter ?? null,
    host: value?.host ?? null,
    support: value?.support ?? null,
});

export const toRoleConnectionFieldValue = (value?: IRoleConnectionsValue | null): IRoleConnectionInput[] => {
    const normalized = normalizeRoleConnections(value);
    const inputs: IRoleConnectionInput[] = [];
    ROLE_CONNECTION_KEYS.forEach(key => {
        const assignment = normalized[key];
        if (!assignment?.id) return;
        if (assignment.type === 'connectionGroup') {
            inputs.push({ role: ROLE_CONNECTION_API_ROLES[key], connectionGroupId: assignment.id });
            return;
        }
        inputs.push({ role: ROLE_CONNECTION_API_ROLES[key], connectionId: assignment.id });
    });
    return inputs;
};

export const fromRoleConnectionFieldValue = (value?: IRoleConnectionInput[] | null): IRoleConnectionsValue => {
    const next = emptyRoleConnections();
    (value || []).forEach(item => {
        const key = ROLE_CONNECTION_KEYS_BY_API_ROLE[item?.role];
        if (!key) return;
        if (item.connectionGroupId) {
            next[key] = { type: 'connectionGroup', id: item.connectionGroupId };
            return;
        }
        if (item.connectionId) {
            next[key] = { type: 'connection', id: item.connectionId };
        }
    });
    return next;
};

export const encodeRoleConnectionOptionId = (assignment: IRoleConnectionAssignment | null): string => {
    return assignment ? `${assignment.type}:${assignment.id}` : CHOOSE_CONNECTION_OPTION_ID;
};

export const decodeRoleConnectionOptionId = (optionId: string): IRoleConnectionAssignment | null => {
    if (!optionId) return null;
    const separator = optionId.indexOf(':');
    if (separator <= 0) return null;
    const type = optionId.slice(0, separator) as RoleConnectionTargetType;
    if (type !== 'connection' && type !== 'connectionGroup') return null;
    const id = optionId.slice(separator + 1);
    if (!id) return null;
    return { type: type, id: id };
};

export const connectionReferenceId = (connection: TConnection): string => {
    return (connection as IOtherConnection).customConnectionId || connection.connectionId;
};

export const roleTargetExists = (
    assignment: IRoleConnectionAssignment | null,
    connections: TConnection[],
    connectionGroups: IConnectionTemplateConnectionGroupDTO[]
): boolean => {
    if (!assignment) return false;
    if (assignment.type === 'connection') {
        return connections.some(connection => connectionReferenceId(connection) === assignment.id || connection.connectionId === assignment.id);
    }
    return connectionGroups.some(group => group.connectionGroupId === assignment.id);
};

export const pruneRoleConnections = (
    value: IRoleConnectionsValue,
    connections: TConnection[],
    connectionGroups: IConnectionTemplateConnectionGroupDTO[]
): IRoleConnectionsValue => {
    const next = normalizeRoleConnections(value);
    ROLE_CONNECTION_KEYS.forEach(key => {
        if (!roleTargetExists(next[key], connections, connectionGroups)) {
            next[key] = null;
        }
    });
    return next;
};

export const resolveRoleConnectionLabel = (
    assignment: IRoleConnectionAssignment | null,
    connections: TConnection[],
    connectionGroups: IConnectionTemplateConnectionGroupDTO[]
): string => {
    if (!assignment) return '';
    if (assignment.type === 'connection') {
        const connection = connections.find(item =>
            connectionReferenceId(item) === assignment.id || item.connectionId === assignment.id
        );
        return connection ? getConnectionTemplateItemLabelText(connection) : '';
    }
    return connectionGroups.find(group => group.connectionGroupId === assignment.id)?.label || '';
};

export const buildRoleConnectionOptions = (
    connections: TConnection[],
    connectionGroups: IConnectionTemplateConnectionGroupDTO[],
    chooseLabel: string
): IRoleConnectionOption[] => {
    const connectionOptions = connections.map(connection => ({
        id: encodeRoleConnectionOptionId({ type: 'connection', id: connectionReferenceId(connection) }),
        label: getConnectionTemplateItemLabelText(connection),
        isGroup: false,
    }));
    const groupOptions = connectionGroups
        .filter(group => Boolean(group.connectionGroupId))
        .map(group => ({
            id: encodeRoleConnectionOptionId({ type: 'connectionGroup', id: group.connectionGroupId }),
            label: group.label,
            isGroup: true,
        }));
    return [
        { id: CHOOSE_CONNECTION_OPTION_ID, label: chooseLabel, isGroup: false },
        ...connectionOptions,
        ...groupOptions,
    ];
};
